import React, { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { countries } from '../../../../constants.js/countries';
import { FieldShell, describedBy } from './Field';
import { InputShell, Input, SelectSegment, Flag } from './Field.styles';

/** LatAm first (docs/signup-v2/02-ux.md §4.2), then alphabetical. */
const PRIORITY = ['CO', 'MX', 'PE', 'EC', 'CL', 'AR', 'VE', 'PA', 'CR', 'GT', 'DO', 'US', 'ES'];

/** ISO 3166-1 alpha-2 → emoji flag (copied from the v1 form, which stays untouched). */
export const countryToFlag = (isoCode) =>
    typeof String.fromCodePoint !== 'undefined'
        ? isoCode
              .toUpperCase()
              .replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
        : isoCode;

/** Dial code with digits only ('1-268' → '1268'). */
export const normalizeDial = (phone) => String(phone || '').replace(/\D/g, '');

const getRegionNames = (language) => {
    try {
        return new Intl.DisplayNames([language], { type: 'region' });
    } catch {
        return null;
    }
};

export const getCountryOptions = (language = 'es') => {
    const regionNames = getRegionNames(language);
    const unique = countries.filter(
        (country, index) => countries.findIndex((c) => c.code === country.code) === index
    );
    const options = unique.map((country) => {
        let name = country.label;
        try {
            name = (regionNames && regionNames.of(country.code)) || country.label;
        } catch {
            name = country.label;
        }
        return {
            code: country.code,
            dial: normalizeDial(country.phone),
            name,
            flag: countryToFlag(country.code),
        };
    });
    const priority = PRIORITY.map((code) => options.find((o) => o.code === code)).filter(Boolean);
    const rest = options
        .filter((o) => !PRIORITY.includes(o.code))
        .sort((a, b) => a.name.localeCompare(b.name, language));
    return [...priority, ...rest];
};

/**
 * WhatsApp number: native country `<select>` (transparent over the "🇨🇴 +57" segment) + tel input.
 */
const PhoneField = forwardRef(
    ({ id, label, isoCode, onCountryChange, error, hint, ...inputProps }, ref) => {
        const { t, i18n } = useTranslation();
        const options = useMemo(() => getCountryOptions(i18n.language), [i18n.language]);
        const current = options.find((o) => o.code === isoCode) || options[0];

        return (
            <FieldShell id={id} label={label} hint={hint} error={error}>
                <InputShell invalid={!!error}>
                    <SelectSegment>
                        <Flag aria-hidden="true">{current.flag}</Flag>
                        <span aria-hidden="true">{`+${current.dial}`}</span>
                        <i className="ri-arrow-down-s-line" aria-hidden="true" />
                        <select
                            id={`${id}-country`}
                            value={current.code}
                            onChange={(event) => onCountryChange(event.target.value)}
                            aria-label={t('SignUpV2.tienda.countryLabel')}
                            autoComplete="tel-country-code"
                        >
                            {options.map((option) => (
                                <option key={option.code} value={option.code}>
                                    {`${option.flag} ${option.name} (+${option.dial})`}
                                </option>
                            ))}
                        </select>
                    </SelectSegment>
                    <Input
                        id={id}
                        ref={ref}
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel-national"
                        aria-invalid={error ? 'true' : 'false'}
                        aria-describedby={describedBy(id, { error, hint })}
                        {...inputProps}
                    />
                </InputShell>
            </FieldShell>
        );
    }
);

export default PhoneField;
